import FileSaver from 'file-saver'
import { LatLng } from 'leaflet'
import bbox from '@turf/bbox'
import _ from 'lodash'
import { lineString } from '@turf/helpers'
import constants from './constants'
import { clearToken } from './token'
import length from '@turf/length'
import area from '@turf/area'
import { format } from 'd3'
import { PhoneNumberUtil } from 'google-libphonenumber'

const phoneUtil = PhoneNumberUtil.getInstance()

export const isPhoneValid = (phone) => {
  try {
    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone))
  } catch (error) {
    return false
  }
}

const EMAIL_REGEX = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/

export const isEmailValid = (email) => EMAIL_REGEX.test(email)

// Darken (amt < 0) or lighten (amt > 0) a hex color, keeping the hue
export const shadeHex = (hex, amt) => {
  if (typeof hex !== 'string' || hex[0] !== '#') return hex
  let h = hex.slice(1)
  if (h.length === 3) h = h.split('').map(c => c + c).join('')
  const num = parseInt(h, 16)
  const adjust = (c) => {
    const target = amt < 0 ? 0 : 255
    return Math.round(c + (target - c) * Math.abs(amt))
  }
  const r = adjust((num >> 16) & 255)
  const g = adjust((num >> 8) & 255)
  const b = adjust(num & 255)
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`
}

// returns the correct icon for a checkbox
export const checkboxIcon = state => (
  state ? 'checkbox-checked' : 'checkbox'
)

export const removeHtml = string => {
  return string.replace(/<(?:.|\n)*?>/gm, '')
}

// cause the browser to download an Excel workbook
export const downloadExcel = async (name, workbook) => {
  const blob = await workbook.outputAsync()
  FileSaver.saveAs(blob, `${name}.xlsx`)
}

// cause the browser to download a CSV file
export const downloadCSV = (name, csvContent) => {
  const contentWithBOM = (typeof csvContent === 'string' && csvContent.startsWith('\ufeff')) ? csvContent : `\ufeff${csvContent}`
  const blob = new Blob([contentWithBOM], { type: 'text/csv;charset=utf-8;' })
  FileSaver.saveAs(blob, `${name}.csv`)
}

// cause the browser to download an KML file
export const downloadKML = (name, xmpString, kml) => {
  const extension = kml ? '.kml' : '.kmz'
  FileSaver.saveAs(xmpString, `${name}${extension}`)
}

// return bounds of a geoJSON geometry in a format that Leaflet accepts
export const geoJSONBounds = (geoJSON) => {
  if (!geoJSON) return null
  try {
    const bounds = bbox(geoJSON)
    if (
      !bounds ||
      bounds.length < 4 ||
      !bounds.every(Number.isFinite) ||
      bounds[0] > bounds[2] ||
      bounds[1] > bounds[3]
    ) {
      return null
    }
    return [[bounds[1], bounds[0]], [bounds[3], bounds[2]]]
  } catch (e) {
    return null
  }
}

export const getShapeArea = (shape) => {
  if (!shape) return 0
  if (shape.geoJSON) {
    try {
      const a = area(shape.geoJSON)
      if (typeof a === 'number' && !isNaN(a) && a > 0) {
        return a
      }
    } catch (e) {}
    try {
      const bounds = geoJSONBounds(shape.geoJSON)
      if (bounds && bounds[0] && bounds[1]) {
        return Math.abs((bounds[1][0] - bounds[0][0]) * (bounds[1][1] - bounds[0][1]))
      }
    } catch (e2) {}
  }
  return 0
}

// send the user to their profile page
export const sendToProfile = (user) => {
  if (user && user.user_type === 'team_member') {
    window.location.href = `${constants.MY_ACCOUNT_URL}/${user.pk}`
  } else {
    window.location.href = constants.MY_PROFILE_URL
  }
}

export const distanceBetweenPoints = (points, finalPoint) => {
  if (points.length < 1) {
    return ''
  }
  const line = lineString(
    [points[points.length - 1], finalPoint]
  )
  const lineLength = length(line, { units: 'kilometers' })
  return `${format('.0f')(lineLength)} km`
}

// clear the user's token and redirect them
// export const logout = () => {
//   window.localStorage.logout = true
//   clearToken()
//   const next = window.encodeURIComponent(window.location.href)
//   window.location.href = `${process.env.LOGOUT_URL}?next=${next}`
// }

export const logout = () => {
  localStorage.removeItem('leftDrawerSortType')
  clearToken()
  const domain = process.env.AUTH0_DOMAIN
  const clientId = process.env.AUTH0_CLIENT_ID
  const returnTo = process.env.LOGOUT_URL
  const logoutUrl = `https://${domain}/v2/logout?client_id=${clientId}&returnTo=${encodeURIComponent(returnTo)}`
  window.location.href = logoutUrl
}

// redirect user to landing page
export const redirectToLandingPage = () => {
  window.location.href = `${process.env.CORPORATE_URL}/`
}

// redirect user to expired popup screen
export const redirectToExpired = () => {
  localStorage.removeItem('leftDrawerSortType')
  // window.location.href = `${process.env.CORPORATE_URL}/users/profile/?expired=true&type=common`
  window.location.href = `${process.env.CORPORATE_URL}/trial-expired/`
}

// redirect user to expired 24h trial access popup screen
export const redirectToExpired24 = () => {
  localStorage.removeItem('leftDrawerSortType')
  // window.location.href = `${process.env.CORPORATE_URL}/users/profile/?expired=true&type=24`
  window.location.href = `${process.env.CORPORATE_URL}/trial-expired/`
}

// Adds a Excel sheet to the specified workbook, and sets
// its contents to be the specified array of arrays.
// The sheet's name is cleaned to make it a valid Excel sheet name
export const sheetFromArrays = (workbook, name, arrays) => {
  // const sheet = workbook.addSheet(name.replace(/[\\/*[\]\\?]/g, ' ').slice(0, 31))
  const sheet = workbook.addSheet(name.replace(/[\\/*[\]:?']/g, ' ').slice(0, 31))
  sheet.range(1, 1, arrays.length, arrays[0].length)
    .value(arrays)
    .style('fontFamily', 'arial')

  return sheet
}

// Returns a Leaflet smooth factor based on the number
// of points in the specified geoJSON geometry. Smooths
// more complex geometries more aggressively.
export const smoothFactor = (geoJSON) => {
  const complexity = _.sumBy(geoJSON.coordinates, x => x[0].length)
  if (complexity > 10000) {
    return 1.6
  } else if (complexity > 1000) {
    return 1.2
  } else if (complexity > 100) {
    return 0.8
  } else {
    return 0.4
  }
}

export const loadScript = (url) => {
  const script = document.createElement('script')
  script.src = url
  script.async = true
  document.body.appendChild(script)
}

export const dispatchKeyPressEvent = (keyCode, keyName, element) => {
  if (document.createEventObject) {
    const eventObj = document.createEventObject()
    eventObj.keyCode = keyCode
    eventObj.which = keyCode
    eventObj.key = keyName
    element.fireEvent('onkeydown', eventObj)
  } else {
    const eventObj = document.createEvent('Events')
    eventObj.initEvent('keydown', true, true)
    eventObj.which = keyCode
    eventObj.keyCode = keyCode
    eventObj.key = keyName
    element.dispatchEvent(eventObj)
  }
}

// Returns the necessary MobX props to make an input element
// track an observable property on an object. Given an object
// and the string name of a property on that object,
// trackValue returns a `value` prop which updates the input's value
// when the property changes, and an `onChange` prop, which
// updates the property when the input's value is changed.
export const trackValue = (object, property) => ({
  value: object[property] === null ? '' : object[property],
  onChange: e => {
    object[property] = e.target.value
  }
})

// returns an SVG translate attribute string
export const translate = (x, y) => `translate(${x}, ${y})`

// given a Turf.js point, return a Leaflet LatLng
export const turfPointToLatLng = (p) => new LatLng(p[1], p[0])

/**
 * Options for population-data API routing (geopolygon vs admin vs zip).
 */
export const getShapePopulationFetchOptions = (shape) => {
  if (!shape) {
    return { isZipCode: false, isAdminBoundary: false }
  }
  const isZipCode = shape.isZipCode === true
  const isAdminBoundary = !isZipCode && shape.remote_db_id != null
  return { isZipCode, isAdminBoundary }
}

export const getPropByRegex = (object, regex) => {
  const key = Object.keys(object).filter((name) => regex.test(name))[0]
  return object[key]
}

export const formatNumber = (num) => {
  if (num == null || isNaN(num)) {
    return '0'
  }

  const numValue = Number(num)

  if (numValue >= 1000000) {
    const val = numValue / 1000000
    return `${Number.isInteger(val) ? val : Number(val.toFixed(1))}M`
  } else if (numValue >= 1000) {
    const val = numValue / 1000
    return `${Number.isInteger(val) ? val : Number(val.toFixed(1))}k`
  } else {
    if (Number.isInteger(numValue)) {
      return numValue.toString()
    }
    return Number(numValue.toFixed(2)).toString()
  }
}

export function alignToStep (value, step, direction = 'nearest') {
  if (value == null) return null
  const inv = 1 / step
  switch (direction) {
    case 'ceil':
      return Math.ceil(value * inv) / inv
    case 'floor':
      return Math.floor(value * inv) / inv
    default:
      return Math.round(value * inv) / inv
  }
}

// Minimal point-in-polygon helpers for GeoJSON (no external deps)
function pointInRing (point, ring) {
  const [x, y] = point // [lng, lat]
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0]
    const yi = ring[i][1]
    const xj = ring[j][0]
    const yj = ring[j][1]
    const intersect = ((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)
    if (intersect) inside = !inside
  }
  return inside
}

function pointInPolygon (point, polygonCoords) {
  // polygonCoords: [ [ [lng,lat], ... ], [hole1], [hole2], ... ]
  if (!polygonCoords || polygonCoords.length === 0) return false
  // outer ring
  const outer = polygonCoords[0]
  if (!pointInRing(point, outer)) return false
  // if in outer, ensure not in any hole
  for (let i = 1; i < polygonCoords.length; i++) {
    if (pointInRing(point, polygonCoords[i])) return false
  }
  return true
}

export function pointInGeoJSON (point, geojson) {
  if (!geojson) return false
  const type = geojson.type
  if (type === 'FeatureCollection') {
    return geojson.features.some(f => pointInGeoJSON(point, f))
  }
  if (type === 'Feature') {
    return pointInGeoJSON(point, geojson.geometry)
  }
  if (!geojson.type || !geojson.coordinates) return false
  if (geojson.type === 'Polygon') {
    return pointInPolygon(point, geojson.coordinates)
  }
  if (geojson.type === 'MultiPolygon') {
    return geojson.coordinates.some(polygonCoords => pointInPolygon(point, polygonCoords))
  }
  return false
}

export function getVectorTileEndpoint (admin_level, id_0, id_1, is_zip_code) {
  return `${constants.VECTOR_TILE_ENDPOINT}?admin_level=${admin_level}&id_0=${id_0}&id_1=${id_1}&is_zip_code=${is_zip_code ? 1 : 0} `
}

export const formatCompactNumber = (
  value,
  {
    minimumFractionDigits = 0,
    maximumFractionDigits = 0,
    compactMinimumFractionDigits = 1,
    compactMaximumFractionDigits = 1
  } = {}
) => {
  const numericValue = typeof value === 'number' ? value : Number(value)
  if (value == null || Number.isNaN(numericValue)) return '0'

  const absValue = Math.abs(numericValue)
  if (absValue >= 10000000) {
    return new Intl.NumberFormat('en-US', {
      notation: 'compact',
      minimumFractionDigits: compactMinimumFractionDigits,
      maximumFractionDigits: compactMaximumFractionDigits
    }).format(numericValue)
  }

  return numericValue.toLocaleString('en-US', {
    minimumFractionDigits,
    maximumFractionDigits
  })
}

export const isGrayColor = (hex) => {
  if (!hex || typeof hex !== 'string') return false
  const clean = hex.trim().toLowerCase()
  if (['#808080', '#cabdbf', '#737373', '#888888', '#9e9e9e', '#a0a0a0', '#666666', '#707070', '#7f7f7f', '#bebebe', '#d3d3d3', '#cccccc', '#aaaaaa', 'gray', 'grey'].includes(clean)) {
    return true
  }
  const match = clean.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i)
  if (match) {
    const r = parseInt(match[1], 16)
    const g = parseInt(match[2], 16)
    const b = parseInt(match[3], 16)
    const maxDiff = Math.max(Math.abs(r - g), Math.abs(g - b), Math.abs(r - b))
    const avg = (r + g + b) / 3
    if (maxDiff <= 20 && avg >= 40 && avg <= 230) {
      return true
    }
  }
  return false
}

export const getDefaultGrayColor = (markerColors) => {
  if (!Array.isArray(markerColors) || markerColors.length === 0) return null
  const exact = markerColors.find(c => (c.active_color || '').toLowerCase() === '#808080')
  if (exact) return exact

  const gray = markerColors.find(c => isGrayColor(c.active_color))
  if (gray) return gray

  return null
}

export const mergePopulationDatas = (lsData, wpData) => {
  if (!lsData && !wpData) return null
  const merged = {}

  const addEntries = (data, defaultType) => {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return
    Object.keys(data).forEach(year => {
      const rawList = Array.isArray(data[year]) ? data[year] : (data[year] ? [data[year]] : [])
      const entries = rawList.map(entry => {
        if (entry && !entry.type && defaultType) {
          return { ...entry, type: defaultType }
        }
        return entry
      }).filter(Boolean)

      if (!merged[year]) {
        merged[year] = [...entries]
      } else {
        entries.forEach(entry => {
          const entryType = entry.type || defaultType
          const existingIdx = merged[year].findIndex(e => (e.type || defaultType) === entryType)
          if (existingIdx >= 0) {
            merged[year][existingIdx] = entry
          } else {
            merged[year].push(entry)
          }
        })
      }
    })
  }

  addEntries(lsData, 'landscan')
  addEntries(wpData, 'worldpop')

  return Object.keys(merged).length > 0 ? merged : null
}
